import {
  MessageSquareText,
  ListChecks,
  Bug,
  Wallet,
  PanelRight,
  Users,
} from "lucide-react";
import Container from "@/components/ui/Container";
import Reveal from "@/components/common/Reveal";
import { cn } from "@/lib/cn";

const FEATURES = [
  {
    icon: MessageSquareText,
    title: "Plain-English tests",
    body: "Describe what to check like you'd tell a teammate. No selectors, no scripts, no flaky XPath.",
    span: "md:col-span-2",
    accent: "from-brand-500/15 to-brand-500/0",
  },
  {
    icon: ListChecks,
    title: "Watch it run, live",
    body: "A step-by-step plan checks off in real time, with a screenshot at every step.",
    span: "",
    accent: "from-sky-500/15 to-sky-500/0",
  },
  {
    icon: Bug,
    title: "Auto bug reports",
    body: "When something fails you get a real report: severity, expected vs actual, and repro steps.",
    span: "",
    accent: "from-rose-500/15 to-rose-500/0",
  },
  {
    icon: Wallet,
    title: "Cost in plain sight",
    body: "Every run shows tokens and dollars — per agent, per test. No surprise AI bills.",
    span: "md:col-span-2",
    accent: "from-emerald-500/15 to-emerald-500/0",
  },
  {
    icon: PanelRight,
    title: "Chrome side-panel",
    body: "Test the page you're already on. The extension grabs the URL and runs from a docked panel.",
    span: "md:col-span-2",
    accent: "from-violet-500/15 to-violet-500/0",
  },
  {
    icon: Users,
    title: "Team & roles",
    body: "Testers run and review; admins manage cost, models, and people. One shared history.",
    span: "",
    accent: "from-amber-500/15 to-amber-500/0",
  },
];

export default function Features() {
  return (
    <section id="features" className="relative scroll-mt-20 py-20 sm:py-28">
      <Container>
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
            Everything in one place
          </span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
            QA that explains itself
          </h2>
          <p className="mt-4 text-slate-600 dark:text-slate-300">
            From the first plain-English instruction to the final bug report —
            with the cost of every step visible.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 md:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.06} className={cn(f.span)}>
              <div className="card group relative h-full overflow-hidden p-6">
                <div
                  className={cn(
                    "pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br blur-2xl transition-opacity duration-300 group-hover:opacity-100 opacity-60",
                    f.accent
                  )}
                />
                <div className="relative">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300">
                    <f.icon size={20} />
                  </span>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                    {f.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}
